import { observable, action, override, keys, set, makeObservable } from 'mobx';

export type IdResource = { id?: unknown };

export interface IResourceSession<T> {
    getItem(key: unknown): T | undefined;
    updateItem(item: T): T;
    updateItems(items: T[]): T[];
}

// Acts as the source of truth for references to Resources of a particular id

export class ResourceSession<T> implements IResourceSession<T> {
    readonly items = observable.map<unknown, T>([]);

    keyOf: (item: T) => unknown;

    constructor(keyFunc: (item: T) => unknown) {
        makeObservable(this);
        this.keyOf = keyFunc;
    }

    getItem(key: unknown): T | undefined {
        if (this.items.has(key)) {
            return this.items.get(key);
        }
    }

    @action
    updateItem(item: T): T {
        const id = this.keyOf(item);
        if (!id) {
            throw new Error('Can only set an item with a key');
        }

        const existingItem: T | undefined = this.items.get(id);
        if (!existingItem) {
            // We add it to the observable map, which causes the item to become observable
            this.items.set(id, item);
            // we load it from the map to get the observable instance we we share out.
            return this.items.get(id)!;
        }

        //update existing object
        const itemKeys = Object.keys(item as object) as (keyof T)[];
        for (const propName of itemKeys) {
            set(existingItem, propName as string, item[propName]);
        }

        //wipe out any excess keys since our items don't include fields which are now null
        const targetKeys = keys(existingItem) as string[];
        for (const propName of targetKeys) {
            if ((itemKeys as string[]).indexOf(propName) === -1) {
                set(existingItem, propName, undefined);
            }
        }

        return existingItem;
    }

    @action
    updateItems(items: T[]): T[] {
        return items.map((i) => this.updateItem(i));
    }
}

// Like a resource session but removes items during updateItems if they don't exist in the items payload
// the assumption being that the owner of a PrivateResourceSession is the only one doing the updating and can clean up
// items that it is sure won't appear in future updates to save ram
export class PrivateResourceSession<T> extends ResourceSession<T> {
    constructor(keyFunc: (item: T) => unknown) {
        super(keyFunc);
        makeObservable(this);
    }

    @override
    updateItems(items: T[]): T[] {
        const updatedItems = items.map((i) => this.updateItem(i));

        //cleanup unused items
        const newItemIds = new Set(items.map((i) => this.keyOf(i)));
        this.items.forEach((_, key, map) => {
            if (!newItemIds.has(key)) {
                map.delete(key);
            }
        });

        return updatedItems;
    }
}
